/**
 * MODULE: core/tenant/frontend/api/tenantApi.ts
 * PURPOSE: Tenant API client - HTTP client for tenant management endpoints
 *
 * DEPENDENCIES:
 *   - ../../contracts/tenant.types: Tenant, CreateTenantRequest, UpdateTenantRequest types
 *   - ../../../../_shared/lib/frontend/api/errors: fetchWith429Retry (HTTP 429 backoff on gateway bursts)
 *
 * PUBLIC API:
 *   - tenantApi.list(): Get tenants for current user (calls /my-tenants endpoint)
 *   - tenantApi.get(id): Get tenant by ID
 *   - tenantApi.getCurrent(): Get current tenant
 *   - tenantApi.create(data): Create new tenant
 *   - tenantApi.update(id, data): Update tenant
 *   - tenantApi.delete(id): Delete tenant
 *   - tenantApi.switch(tenantId): Switch to different tenant
 *
 * SIDE EFFECTS:
 *   - HTTP requests to /api/v1/tenants endpoints
 *   - Reads/writes localStorage for auth token
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 127 / 200 lines
 *
 * CHANGES:
 *   - 2026-06-08 [2.2.1335]: **`getAuthHeaders()`** on all tenant API calls including **`/my-tenants`** (fixes 401 on Azure).
 *   - 2026-05-08 [2.2.874]: **`handleResponse`** maps **HTTP 429** to a user-facing **`Error`** before JSON parse; pairs root **`gatewayRateLimit`** + **`fetchWith429Retry`**. [2.2.782]: **`fetchWith429Retry`** …
 *   - 2026-01-19: Fixed tenantApi.list() to call /my-tenants endpoint instead of /
 *     This ensures regular users get their tenants only, not all tenants (admin-only)
 *     Related: Tenant membership fix for test@example.com user
 *   - 2026-01-19: Fixed API_BASE to use /api/v1/tenants to match backend router prefix
 *     Backend expects /api/v1/tenants (API_PREFIX + "/tenants" where API_PREFIX = '/api/v1')
 *
 * See: /README.md#documentation | /ARCHITECTURE.md
 */

import type {
  Tenant,
  CreateTenantRequest,
  UpdateTenantRequest,
} from '../../contracts/tenant.types';
import { getAuthHeaders } from '../../../../_shared/lib/frontend/api/config';
import { fetchWith429Retry } from '../../../../_shared/lib/frontend/api/errors';

const API_BASE = '/api/v1/tenants';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json();
}

export const tenantApi = {
  async list(): Promise<Tenant[]> {
    // Use /my-tenants endpoint to get tenants for the current user
    // This queries tenant_memberships table to find tenants the user belongs to
    const response = await fetchWith429Retry(`${API_BASE}/my-tenants`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<Tenant[]>(response);
  },

  async get(id: string): Promise<Tenant> {
    const response = await fetchWith429Retry(`${API_BASE}/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<Tenant>(response);
  },

  async getCurrent(): Promise<Tenant> {
    const response = await fetchWith429Retry(`${API_BASE}/current`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<Tenant>(response);
  },

  async create(data: CreateTenantRequest): Promise<Tenant> {
    const response = await fetchWith429Retry(API_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Tenant>(response);
  },

  async update(id: string, data: UpdateTenantRequest): Promise<Tenant> {
    const response = await fetchWith429Retry(`${API_BASE}/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Tenant>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWith429Retry(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Delete failed' }));
      throw new Error(error.message);
    }
  },

  async switch(tenantId: string): Promise<Tenant> {
    const response = await fetchWith429Retry(`${API_BASE}/${tenantId}/switch`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<Tenant>(response);
  },
};
